"use client";

import React, { useState, useEffect } from 'react';
import { Sparkles, GraduationCap, Clock, Award, BrainCircuit, BookMarked, Users, Link as LinkIcon, Github, Mail, FileCheck, FileText, ChevronLeft, ChevronRight } from 'lucide-react';

// A single component for displaying a feature card with an icon, title, and description.
const FeatureCard = ({ title, description, icon }) => (
  <div className="flex flex-col items-center text-center p-6 bg-white rounded-xl shadow-lg transform transition-all hover:scale-[1.02] hover:shadow-2xl">
    <div className="text-4xl text-teal-600 mb-4">{icon}</div>
    <h3 className="text-xl font-bold text-slate-900 mb-2">{title}</h3>
    <p className="text-slate-600">{description}</p>
  </div>
);

// A section dedicated to highlighting the key features of the application.
const FeaturesSection = () => (
  <section id="features" className="w-full max-w-7xl py-12 px-4 sm:px-6 lg:px-8">
    <div className="text-center mb-12">
      <h3 className="text-3xl font-bold text-slate-900 mb-4">
        Key Features for Educators
      </h3>
      <p className="text-lg text-slate-600">
        Craft compelling letters with a fraction of the effort.
      </p>
    </div>
    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
      <FeatureCard
        title="AI-Powered Content Generation"
        description="Generate drafts instantly based on student information and your professional input."
        icon={<BrainCircuit size={48} />}
      />
      <FeatureCard
        title="Customizable Templates"
        description="Choose from a library of professional templates tailored for different college programs and scholarships."
        icon={<BookMarked size={48} />}
      />
      <FeatureCard
        title="Student Profile Integration"
        description="Import student achievements, activities, and goals to create a truly personalized LOR."
        icon={<Users size={48} />}
      />
    </div>
  </section>
);

// A simple call-to-action component to encourage user engagement.
const CallToAction = () => (
  <section className="w-full max-w-7xl py-12 px-4 text-center">
    <div className="bg-gradient-to-r from-orange-600 to-red-700 text-white rounded-3xl p-8 sm:p-12 md:p-16 shadow-xl">
      <h3 className="text-3xl sm:text-4xl font-bold mb-4">
        Ready to Empower Your Students?
      </h3>
      <p className="text-lg mb-8">
        Streamline your workflow and provide students with the powerful letters of
        recommendation they deserve.
      </p>
      <a
        href="/lor"
        className="px-8 py-4 bg-white text-orange-700 font-bold rounded-full text-lg shadow-md hover:bg-slate-100 transition transform hover:scale-105"
      >
        Start Crafting
      </a>
    </div>
  </section>
);

// The footer component containing social links and copyright information.
const Footer = () => (
  <footer className="w-full max-w-7xl flex flex-col items-center py-8 px-4 border-t border-gray-200 mt-12">
    <div className="flex space-x-6 text-gray-500 mb-4">
      <a href="#" className="hover:text-teal-600 transition" aria-label="Link">
        <LinkIcon size={24} />
      </a>
      <a href="#" className="hover:text-teal-600 transition" aria-label="GitHub">
        <Github size={24} />
      </a>
      <a href="#" className="hover:text-teal-600 transition" aria-label="Email">
        <Mail size={24} />
      </a>
    </div>
    <p className="text-gray-500 text-sm">
      &copy; {new Date().getFullYear()} Rec-Builder.com
    </p>
  </footer>
);

// A section to display a user testimonial with a background effect.
const testimonials = [
  {
    quote: "This platform has saved me countless hours. The AI-generated drafts are a fantastic starting point, and with my personal touch, I can create truly meaningful letters for my students.",
    author: "Judith Black",
    title: "Educator at City High School",
    avatar: "/em-code-2.png",
  },
  {
    quote: "I used to dread writing recommendation letters, but Rec-Builder has completely changed the process. The student profile integration is a game-changer for personalization.",
    author: "Michael Chen",
    title: "Head of Counseling at Northwood Academy",
    avatar: "/em-code-2.png",
  },
  {
    quote: "The quality of the letters is exceptional. It helps me focus on providing the best possible support to my students' applications, knowing the letters are professional and impactful.",
    author: "Sarah Lopez",
    title: "Professor at State University",
    avatar: "/em-code-2.png",
  },
];

const TestimonialSection = () => {
  const [currentTestimonial, setCurrentTestimonial] = useState(0);

  const nextTestimonial = () => {
    setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setCurrentTestimonial((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  useEffect(() => {
    const timer = setInterval(() => {
      nextTestimonial();
    }, 10000); // Change testimonial every 10 seconds
    return () => clearInterval(timer);
  }, [currentTestimonial]);

  const { quote, author, title, avatar } = testimonials[currentTestimonial];

  return (
    <section className="relative isolate overflow-hidden bg-white px-6 py-24 sm:py-32 lg:px-8 mt-12 rounded-3xl">
      <div className="absolute inset-0 -z-10 bg-[radial-gradient(45rem_50rem_at_top,var(--color-teal-500),transparent)] opacity-10" />
      <div className="absolute inset-y-0 right-1/2 -z-10 mr-16 w-[200%] origin-bottom-left skew-x-[-30deg] bg-slate-900 shadow-xl ring-1 shadow-teal-500/5 ring-white/5 sm:mr-28 lg:mr-0 xl:mr-16 xl:origin-center" />
      <div className="mx-auto max-w-2xl lg:max-w-4xl relative">
        <img
          alt=""
          src="/rec-5.png"
          className="mx-auto h-12"
        />
        <button
          onClick={prevTestimonial}
          className="absolute left-0 top-1/2 -translate-y-1/2 p-2 rounded-full text-slate-500 hover:text-teal-600 transition"
          aria-label="Previous testimonial"
        >
          <ChevronLeft size={32} />
        </button>
        <button
          onClick={nextTestimonial}
          className="absolute right-0 top-1/2 -translate-y-1/2 p-2 rounded-full text-slate-500 hover:text-teal-600 transition"
          aria-label="Next testimonial"
        >
          <ChevronRight size={32} />
        </button>
        <figure className="mt-10 transition-opacity duration-1000 ease-in-out">
          <blockquote className="text-center text-xl/8 font-semibold text-orange-300 sm:text-2xl/9">
            <p>“{quote}”</p>
          </blockquote>
          <figcaption className="mt-10">
            <img
              alt=""
              src={avatar}
              className="mx-auto size-10 rounded-full"
            />
            <div className="text-orange-300 mt-4 flex items-center justify-center space-x-3 text-base">
              <div className="font-semibold text-orange-300">{author}</div>
              <svg width={3} height={3} viewBox="0 0 2 2" aria-hidden="true" className="fill-white">
                <circle r={1} cx={1} cy={1} />
              </svg>
              <div className="text-black">{title}</div>
            </div>
          </figcaption>
        </figure>
        <div className="mt-8 flex justify-center space-x-2">
          {testimonials.map((_, index) => (
            <div
              key={index}
              className={`h-2 w-2 rounded-full transition-colors ${index === currentTestimonial ? 'bg-teal-600' : 'bg-gray-300'}`}
              onClick={() => setCurrentTestimonial(index)}
              aria-label={`Go to testimonial ${index + 1}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

// Main App component to be exported
const App = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-100 to-gray-200 flex flex-col items-center justify-center p-4 sm:p-6 lg:p-8 font-sans">
      <div className="bg-white rounded-3xl shadow-xl overflow-hidden max-w-4xl w-full p-6 sm:p-10 text-slate-800">
        {/* Header Section */}
        <div className="mt-18 text-center mb-10">
          <Sparkles className="mx-auto h-16 w-16 text-teal-600 mb-4 animate-pulse" />
          <h1 className="text-4xl sm:text-5xl font-extrabold text-slate-900 leading-tight">
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-teal-500 to-orange-500">
              Rec-Builder
            </span>
          </h1>
          <p className="mt-4 text-lg text-slate-600 max-w-prose mx-auto">
            Effortlessly craft personalized and impactful letters of recommendation for your students with the power of AI.
          </p>
        </div>

        {/* Content Section */}
        <div className="space-y-8">
          <p className="text-slate-700 leading-relaxed text-base sm:text-lg">
            Our platform is designed to revolutionize the way educators write letters of recommendation. We understand the value of your time and the importance of a compelling letter. By leveraging advanced artificial intelligence, our tool helps you generate well-structured, eloquent, and highly personalized letters that highlight your students' unique achievements and qualities.
          </p>
          <p className="text-slate-700 leading-relaxed text-base sm:text-lg">
            Say goodbye to writer's block and the hours spent on tedious writing tasks. With just a few key inputs about your student—their academic performance, extracurricular activities, and character traits—our AI models can draft a complete letter for you. You remain in complete control, with the ability to edit, refine, and add your personal touch before finalizing the document.
          </p>
        </div>

        {/* Features Section */}
        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 gap-8">
          <div className="flex items-start bg-white p-6 rounded-2xl shadow-sm">
            <Clock className="h-10 w-10 text-teal-600 mr-4 mt-1" />
            <div>
              <h3 className="text-xl font-semibold text-slate-900">Save Valuable Time</h3>
              <p className="mt-2 text-slate-600">
                Drastically reduce the time spent on writing letters, freeing you up to focus on teaching and student mentorship.
              </p>
            </div>
          </div>
          <div className="flex items-start bg-white p-6 rounded-2xl shadow-sm">
            <Award className="h-10 w-10 text-teal-600 mr-4 mt-1" />
            <div>
              <h3 className="text-xl font-semibold text-slate-900">Enhance Quality & Impact</h3>
              <p className="mt-2 text-slate-600">
                Generate professional, well-written letters that make a lasting impression on admissions committees and potential employers.
              </p>
            </div>
          </div>
          <div className="flex items-start bg-white p-6 rounded-2xl shadow-sm">
            <GraduationCap className="h-10 w-10 text-teal-600 mr-4 mt-1" />
            <div>
              <h3 className="text-xl font-semibold text-slate-900">Tailored for Every Student</h3>
              <p className="mt-2 text-slate-600">
                Our system ensures each letter is unique and highlights the specific strengths and accomplishments of each individual student.
              </p>
            </div>
          </div>
          <div className="flex items-start bg-white p-6 rounded-2xl shadow-sm">
            <Sparkles className="h-10 w-10 text-teal-600 mr-4 mt-1" />
            <div>
              <h3 className="text-xl font-semibold text-slate-900">Your Expertise, Amplified</h3>
              <p className="mt-2 text-slate-600">
                The AI acts as a powerful assistant, not a replacement. You provide the insights, and the AI crafts the perfect words.
              </p>
            </div>
          </div>
          <div className="flex items-start bg-white p-6 rounded-2xl shadow-sm">
            <FileCheck className="h-10 w-10 text-teal-600 mr-4 mt-1" />
            <div>
              <h3 className="text-xl font-semibold text-slate-900">ATS-Friendly Formatting</h3>
              <p className="mt-2 text-slate-600">
                Ensure your letters are easily parsed and read by Applicant Tracking Systems for maximum visibility.
              </p>
            </div>
          </div>
          <div className="flex items-start bg-white p-6 rounded-2xl shadow-sm">
            <FileText className="h-10 w-10 text-teal-600 mr-4 mt-1" />
            <div>
              <h3 className="text-xl font-semibold text-slate-900">ATS-Friendly Resume Scan</h3>
              <p className="mt-2 text-slate-600">
                Quickly upload a student's resume for an automated scan to highlight keywords and accomplishments that will resonate with admissions officers.
              </p>
            </div>
          </div>
        </div>

        {/* Testimonial Section */}
        <TestimonialSection />

        {/* Call to Action */}
        <div className="mt-12 text-center">
          <a
            href="/lor"
            className="inline-block px-8 py-4 text-lg font-semibold text-white bg-gradient-to-r from-teal-600 to-orange-600 rounded-full hover:from-teal-700 hover:to-orange-700 transition-colors duration-300 shadow-md transform hover:scale-105"
          >
            Start Building
          </a>
        </div>
      </div>
    </div>
  );
};

export default App;